import { CardImage } from "@/components/CardImage";
import { SS01_CARDS } from "@/lib/ss01-cards";

export type ContextVisualVariant =
  | "wiki"
  | "tcg"
  | "items"
  | "passives"
  | "technology"
  | "guides"
  | "breeding"
  | "damage"
  | "mounts"
  | "capture"
  | "teams"
  | "elements";

const GAMEPLAY: Record<Exclude<ContextVisualVariant, "tcg">, { src: string; alt: string; caption: string }> = {
  wiki: {
    src: "/images/palworld-gameplay/exploration-world.webp",
    alt: "Player riding a flying Pal across the Palpagos Islands in Palworld",
    caption: "Explore Palpagos with Pals across land, sea and sky.",
  },
  guides: {
    src: "/images/palworld-gameplay/pal-companions.webp",
    alt: "Two Palworld Pals standing together beneath cherry blossoms",
    caption: "Palworld guides for companions, exploration, combat and progression.",
  },
  items: {
    src: "/images/palworld-gameplay/weapons-combat.webp",
    alt: "Palworld Pal carrying a multi-barrel weapon during gameplay",
    caption: "Weapons, equipment and crafted technology used in Palworld.",
  },
  passives: {
    src: "/images/palworld-gameplay/character-pal.webp",
    alt: "Palworld character riding a flower Pal during gameplay",
    caption: "Pal traits shape combat, work speed and movement builds.",
  },
  technology: {
    src: "/images/palworld-gameplay/base-building.webp",
    alt: "Busy automated Palworld base with Pals working at production stations",
    caption: "Technology unlocks power Palworld bases, crafting and automation.",
  },
  breeding: {
    src: "/images/palworld-gameplay/breeding-pals.webp",
    alt: "Two Palworld Pals together in a blossom-filled breeding area",
    caption: "Breed compatible Pals to hatch a target species and inherit useful traits.",
  },
  damage: {
    src: "/images/palworld-gameplay/boss-combat.webp",
    alt: "Player fighting a large water Pal during a Palworld boss encounter",
    caption: "Element matchups and attack stats determine damage in live combat.",
  },
  mounts: {
    src: "/images/palworld-gameplay/flying-mount.webp",
    alt: "Player riding a fast flying Pal through the Palworld landscape",
    caption: "Flying mounts combine travel speed, stamina and aerial access.",
  },
  capture: {
    src: "/images/palworld-gameplay/capture-battle.webp",
    alt: "Player and Pals battling wild creatures before a Palworld capture attempt",
    caption: "Weaken wild Pals and choose the right sphere before attempting a capture.",
  },
  teams: {
    src: "/images/palworld-gameplay/pal-team.webp",
    alt: "A Palworld player exploring with a varied team of Pals",
    caption: "Balanced teams cover combat, travel and field utility.",
  },
  elements: {
    src: "/images/palworld-gameplay/elemental-combat.webp",
    alt: "Player aiming at an Ice Pal during an elemental Palworld battle",
    caption: "Element advantages change the damage dealt during Pal battles.",
  },
};

const LABELS: Record<ContextVisualVariant, string> = {
  wiki: "Palworld exploration and gameplay",
  tcg: "Explore Palworld TCG cards",
  items: "Palworld weapons and equipment",
  passives: "Palworld passive skill builds",
  technology: "Palworld base technology",
  guides: "Palworld companions and guides",
  breeding: "Palworld breeding",
  damage: "Palworld boss combat",
  mounts: "Palworld flying mounts",
  capture: "Palworld capture battles",
  teams: "Palworld teams",
  elements: "Palworld elemental combat",
};

export function guideVisualVariant(text: string): Exclude<ContextVisualVariant, "tcg"> {
  const value = text.toLowerCase();
  if (/breed|egg|hatch|passive|trait/.test(value)) return "breeding";
  if (/mount|fly|flight|travel|speed/.test(value)) return "mounts";
  if (/capture|catch|sphere/.test(value)) return "capture";
  if (/base|build|craft|technology|tech|work|farm|production/.test(value)) return "technology";
  if (/damage|boss|combat|weapon|raid|dungeon/.test(value)) return "damage";
  if (/element|weakness|type/.test(value)) return "elements";
  if (/team|party|squad|pvp/.test(value)) return "teams";
  return "guides";
}

export function gameplayImagePath(variant: Exclude<ContextVisualVariant, "tcg">): string {
  return GAMEPLAY[variant].src;
}

export function ContextVisualStrip({ variant }: { variant: ContextVisualVariant }) {
  if (variant === "tcg") {
    return (
      <figure className="mt-6" aria-label={LABELS[variant]}>
        <div className="flex max-w-md gap-3">
          {SS01_CARDS.slice(0, 3).map((card) => (
            <a key={card.code} href="/guides/palworld-tcg-sleeve-card-set-vol-1/card-list" className="min-w-0 flex-1">
              <CardImage src={card.image} alt={`${card.nameEn} ${card.code} Palworld TCG card`} width={126} height={176} responsive className="w-full rounded-lg border border-border/60" />
            </a>
          ))}
        </div>
        <figcaption className="mt-2 text-xs text-muted-foreground">
          Official card art, including the new Sleeve &amp; Card Set Vol.1 reveal.
        </figcaption>
      </figure>
    );
  }

  const image = GAMEPLAY[variant];
  return (
    <figure className="mt-6 max-w-3xl overflow-hidden rounded-lg border border-border/60 bg-card/40" aria-label={LABELS[variant]}>
      <img
        src={image.src}
        alt={image.alt}
        width={1280}
        height={720}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="aspect-video w-full object-cover"
      />
      <figcaption className="px-3 py-2 text-xs text-muted-foreground">
        {image.caption} <span className="whitespace-nowrap">Image: Pocketpair / Steam.</span>
      </figcaption>
    </figure>
  );
}